import { gql, DocumentNode } from '@apollo/client';
import {
	Box,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Popper,
	Select,
	Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Icon } from '../../../../../blocks/icons';
import { Button } from '../../../../Button';
import MuiCurrencyInput from '../../../../../mui/MuiCurrencyInput/MuiCurrencyInput';
import MuiNumberInput from '../../../../../mui/MuiNumberInput';
import styles from './InvoiceEditLineItemDiscount.module.scss';
import { InvoiceEditLineItemDiscountInvoiceFragment } from '#types';
import rpcShared from '@rockpapercoin/rpc-shared';

const invoiceEditLineItemDiscountInvoiceFragment = gql`
	fragment invoiceEditLineItemDiscountInvoice on Invoice {
		discountAmount
		discountPercentage
		discountIsPercentage
		lineItems {
			id
			name
			quantity
			rate
			taxPercentage
		}
	}
`;

enum CalculationMethods {
	Percentage = 'Percentage',
	Dollar = 'Dollar',
}

export type InvoiceEditLineItemDiscountProps = {
	invoice?: Omit<InvoiceEditLineItemDiscountInvoiceFragment, 'id'> & {
		id?: string;
	};
	setInvoice: React.Dispatch<
		React.SetStateAction<
			Omit<InvoiceEditLineItemDiscountInvoiceFragment, 'id'> & { id?: string }
		>
	>;
	className?: string;
	anchorEl?: HTMLElement | null;
};

type InvoiceEditLineItemDiscountPropsWithFragment =
	React.FC<InvoiceEditLineItemDiscountProps> & {
		fragments: {
			invoiceEditLineItemDiscountInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditLineItemDiscount: InvoiceEditLineItemDiscountPropsWithFragment =
	( { invoice, setInvoice, className, anchorEl } ) => {
		const subtotal = rpcShared.invoices.calculateInvoiceSubtotal(
			invoice?.lineItems
		);

		const [ localDiscount, setLocalDiscount ] = useState<
			Omit<InvoiceEditLineItemDiscountInvoiceFragment, 'id' | 'lineItems'> & {
				id?: string;
			}
		>();
		const [ open, setOpen ] = useState( false );

		/* because the shared library concludes that if discountPercentage > 0 then re-do the math,
		but if it's zero then use discountAmount, setting discountPercentage to zero doesn't
		re-calculate the discountAmount. So we need this helper to do that math for us here. */
		const getAmountFromPercentage = useCallback(
			// we gotta round because calculateInvoiceDiscount returns decimal values
			( discountPercentage?: number | null | undefined ) =>
				!discountPercentage || discountPercentage <= 0
					? 0
					: Math.round( ( discountPercentage * subtotal ) / 100 ),
			[ subtotal ]
		);

		const getPercentageFromAmount = useCallback(
			( discountAmount?: number | null | undefined ) =>
				discountAmount
					? parseFloat( ( ( discountAmount / subtotal ) * 100 ).toPrecision( 4 ) )
					: 0,
			[ subtotal ]
		);

		const invoiceDiscountIsChanging = useCallback(
			(
				prevState?: Omit<
					InvoiceEditLineItemDiscountInvoiceFragment,
					'id' | 'lineItems'
				> & { id?: string },
				newState?: Omit<
					InvoiceEditLineItemDiscountInvoiceFragment,
					'id' | 'lineItems'
				> & { id?: string }
			) =>
				prevState?.discountAmount !== newState?.discountAmount ||
				prevState?.discountPercentage !== newState?.discountPercentage ||
				prevState?.discountIsPercentage !== newState?.discountIsPercentage,
			[]
		);

		useEffect( () => {
			if ( invoice ) {
				setInvoice( ( prevState ) =>
					invoiceDiscountIsChanging( prevState, invoice )
						? {
							...prevState,
							discountAmount: invoice.discountAmount,
							discountPercentage: invoice.discountPercentage,
							discountIsPercentage: invoice.discountIsPercentage,
						  }
						: prevState
				);
				setLocalDiscount( ( prevState ) =>
					invoiceDiscountIsChanging( prevState, invoice )
						? {
							discountAmount: invoice.discountAmount,
							discountPercentage: invoice.discountPercentage,
							discountIsPercentage: invoice.discountIsPercentage,
						  }
						: prevState
				);
			}
		}, [
			invoice,
			invoiceDiscountIsChanging,
			setInvoice
		] );

		const handleClick = () => {
			setOpen( ( previousOpen ) => !previousOpen );
		};

		const canBeOpen = open && Boolean( anchorEl );
		const id = canBeOpen ? 'spring-popper' : undefined;

		// ClickAwayListener doesn't like portaled select
		const containerRef = useRef<HTMLDivElement>( null );
		const menuRef = useRef<HTMLDivElement>( null );

		useEffect( () => {
			const onMouseUp = ( e: MouseEvent | TouchEvent ) => {
				if (
					!containerRef.current?.contains( e.target as Node | null ) &&
					!menuRef.current?.contains( e.target as Node | null )
				) {
					setOpen( false );
				}
			};
			window.addEventListener( 'mouseup', onMouseUp );
			window.addEventListener( 'touchend', onMouseUp );
			return () => {
				window.removeEventListener( 'mouseup', onMouseUp );
				window.removeEventListener( 'touchend', onMouseUp );
			};
		}, [] );

		const labelId = useId();
		return (
			<>
				<IconButton onClick={ handleClick } className={ className }>
					<Icon type='pencil' className={ styles.icon } />
				</IconButton>
				<Popper
					id={ id }
					open={ open }
					className={ styles.container }
					placement='bottom-start'
					disablePortal
					anchorEl={ anchorEl }
				>
					<Box ref={ containerRef }>
						<Typography variant='h5' className={ styles.header }>
							Invoice discount
						</Typography>
						<Box className={ styles.body }>
							<Typography>Apply a discount to the invoice total.</Typography>
							<Box className={ styles.inputs }>
								<FormControl className={ styles.select }>
									<InputLabel id={ labelId }>Input type</InputLabel>
									<Select<CalculationMethods>
										label='Input type'
										labelId={ labelId }
										MenuProps={ { ref: menuRef } }
										value={
											localDiscount?.discountIsPercentage
												? CalculationMethods.Percentage
												: CalculationMethods.Dollar
										}
										onChange={ ( e ) =>
											setLocalDiscount( ( prevState ) => ( {
												...prevState,
												discountIsPercentage:
													e.target.value === CalculationMethods.Percentage,
												discountAmount: 0,
												discountPercentage: 0,
											} ) )
										}
									>
										<MenuItem value={ CalculationMethods.Dollar }>$</MenuItem>
										<MenuItem value={ CalculationMethods.Percentage }>%</MenuItem>
									</Select>
								</FormControl>
								{ localDiscount?.discountIsPercentage ? (
									<MuiNumberInput
										label='Discount percentage'
										autoFocus
										value={ localDiscount.discountPercentage || undefined }
										slotProps={ {
											input: {
												endAdornment: '%',
											},
											htmlInput: {
												'data-cy': 'discountPercentage',
												inputMode: 'decimal',
											},
										} }
										onChange={ ( discountPercentage ) =>
											setLocalDiscount( ( prevState ) => ( {
												...prevState,
												discountPercentage,
												discountAmount:
													getAmountFromPercentage( discountPercentage ),
											} ) )
										}
										max={ 100 }
										min={ 0 }
										limit={ /^\d{0,3}(\.\d{0,2})?$/ }
									/>
								) : (
									<MuiCurrencyInput
										label='Discount amount'
										autoFocus
										value={ localDiscount?.discountAmount }
										slotProps={ {
											input: {
												startAdornment: '$',
											},
											htmlInput: {
												'data-cy': 'discountAmount',
												inputMode: 'decimal',
											},
										} }
										onChange={ ( discountAmount ) =>
											setLocalDiscount( ( prevState ) => ( {
												...prevState,
												discountAmount,
												discountPercentage:
													getPercentageFromAmount( discountAmount ),
											} ) )
										}
										limit={ /^\d+(\.\d{0,2})?$/ }
									/>
								) }
							</Box>
						</Box>
						<Box className={ styles.buttons }>
							<Button
								variant='text'
								onClick={ () => {
									setInvoice( ( prevState ) => ( {
										...prevState,
										discountIsPercentage: false,
										discountAmount: 0,
										discountPercentage: 0,
									} ) );
									setLocalDiscount( {
										discountIsPercentage: false,
										discountAmount: 0,
										discountPercentage: 0,
									} );
									setOpen( false );
								} }
							>
								Remove
							</Button>
							<Button
								variant='text'
								onClick={ () => {
									setInvoice( ( prevState ) => ( {
										...prevState,
										...localDiscount,
									} ) );
									setOpen( false );
								} }
							>
								Apply
							</Button>
						</Box>
					</Box>
				</Popper>
			</>
		);
	};

InvoiceEditLineItemDiscount.fragments = {
	invoiceEditLineItemDiscountInvoiceFragment,
};
